/**
 * @file task.tab.tsx
 * @description Таба "Задача" формы баг-репорта для связывания дефекта с задачей.
 * Позволяет выбрать задачу через Dictionary компонент и отображает детальную информацию о выбранной задаче:
 * название, номер, исполнитель, статус, спринт, сервис, проект, плановые и фактические даты начала/окончания.
 * Автоматически загружает полные данные задачи через ObjectFormService при выборе и форматирует временные метки.
 * Отображает информацию в read-only режиме для ознакомления с контекстом связанной задачи.
 * @author AI Assistant
 * @created 2025-09-04
 * @updated 2025-09-04
 * @dependencies [react, @src/types, @components/form-controls/dictionary, @api/actions, @components/ui/data/dictionary, @components/ui/base/text-input, @utils/date-time-helper]
 * @related-files [@forms/bug/bug.form.tsx, @api/actions/object-form.ts, @utils/date-time-helper.ts]
 */

import { useEffect, useState } from 'react';
import { tabProps } from '@src/types';
import { Dictionary } from '@components/form-controls/dictionary';
import { ObjectFormService } from '@api/actions';
import { Dictionary as UIDictionary } from '@components/ui/data/dictionary';
import { TextInput } from '@components/ui/base/text-input';
import { formatTimestamp } from '@utils/date-time-helper';
import { isAxiosError } from 'axios';

export const TaskTab = ({ control, getValues }: tabProps) => {
    const [task, setTask] = useState<any>(getValues ? getValues('task') : null);
    const [taskData, setTaskData] = useState<any>(null);

    const fetchTask = async (task: any) => {
        const taskData = await ObjectFormService.get(task.$type, task.id);

        if (isAxiosError(taskData)) {
            return;
        }

        setTaskData(taskData.data);
    };

    useEffect(() => {
        task?.id ? fetchTask(task) : setTaskData(null);
    }, [task]);

    return (
        <div className="flex flex-col gap-default">
            <Dictionary
                control={control}
                label="Задача"
                name="task"
                onChangeHandler={(_name: string, value: any) => setTask(value)}
                width="29vmin"
            />

            {taskData && (
                <div className="flex flex-col gap-default w-[41vmin] bg-grey p-default">
                    <div className="flex gap-default">
                        <TextInput value={taskData.name} label="Название" />

                        <TextInput value={taskData.number} label="Номер" />
                    </div>

                    <UIDictionary
                        value={taskData.executor}
                        isCanSearch={false}
                        isCanClear={false}
                        label="Исполнитель"
                    />

                    <div className="flex gap-default">
                        <TextInput value={taskData.state?.name} label="Статус" readOnly={true} />

                        <UIDictionary value={taskData.sprint} isCanSearch={false} isCanClear={false} label="Спринт" />
                    </div>

                    <div className="flex gap-default">
                        <UIDictionary value={taskData.service} isCanSearch={false} isCanClear={false} label="Сервис" />

                        <UIDictionary value={taskData.project} isCanSearch={false} isCanClear={false} label="Проект" />
                    </div>

                    <div className="flex gap-default">
                        <TextInput
                            value={formatTimestamp(taskData.dateStartPlan, 'DD.MM.YYYY HH:mm')}
                            label="Плановое начало"
                            readOnly={true}
                        />

                        <TextInput
                            value={formatTimestamp(taskData.dateStartFact, 'DD.MM.YYYY HH:mm')}
                            label="Фактическое начало"
                            readOnly={true}
                        />
                    </div>

                    <div className="flex gap-default">
                        <TextInput
                            value={formatTimestamp(taskData.dateEndPlan, 'DD.MM.YYYY HH:mm')}
                            label="Плановое окончание"
                            readOnly={true}
                        />

                        <TextInput
                            value={formatTimestamp(taskData.dateEndFact, 'DD.MM.YYYY HH:mm')}
                            label="Фактическое окончание"
                            readOnly={true}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
